import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  OnInit,
  Renderer2,
  signal,
  ViewChild,
} from "@angular/core";
import { HttpService } from "@app/core/services";
import { FormControl, FormGroup, Validators } from "@angular/forms";
import { timeout } from "rxjs/operators";
import { Router } from "@angular/router";
import { LocalStorageService } from "ngx-webstorage";
import { ReportNotificationService } from "@app/core/services/report-notification.service";
import { TranslateService } from "@ngx-translate/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { DesignKind, DesignService } from "@app/core/services/design.service";
import { Observable } from "rxjs";

interface InputPosition {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface TrianglePosition {
  width: string;
  height: string;
  left: string;
  top: string;
}

interface LogoSvgPosition {
  logoX: string;
  logoY: string;
  logoWidth: string;
  logoHeight: string;
}

interface MaskStyle {
  "mask-image": string;
  "-webkit-mask-image": string;
  "mask-composite": string;
  "-webkit-mask-composite": string;
  "mask-repeat": string;
  "-webkit-mask-repeat": string;
  "mask-position": string;
  "-webkit-mask-position": string;
  "mask-size": string;
  "-webkit-mask-size": string;
}

@Component({
  selector: "app-login-new",
  templateUrl: "./login-new.component.html",
  styleUrls: ["./login-new.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class LoginNewComponent implements OnInit, AfterViewInit {
  @ViewChild("overlay") overlay!: ElementRef<HTMLDivElement>;
  @ViewChild("overlayFive") overlayFive!: ElementRef<HTMLDivElement>;
  @ViewChild("blurMask") blurMask!: ElementRef<HTMLDivElement>;
  @ViewChild("input1") input1!: ElementRef<HTMLInputElement>;
  @ViewChild("input2") input2!: ElementRef<HTMLInputElement>;
  @ViewChild("logoSvg") logoSvg!: ElementRef<HTMLInputElement>;
  email = "";
  password = "";
  readonly borderRadius = 10;
  readonly padding = 0;
  public isLoading = signal<boolean>(false);
  public errorStatusMsg = signal<string | null>(null);
  loginForm = new FormGroup({
    login: new FormControl({ value: "", disabled: false }, [
      Validators.minLength(3),
      Validators.required,
    ]),
    password: new FormControl({ value: "", disabled: false }, [
      Validators.minLength(3),
      Validators.required,
    ]),
  });
  protected readonly BORDER_RADIUS_INPUT_CUTOUT = 10;
  protected readonly positionInputData = signal<InputPosition[]>([]);
  protected readonly positionLogoSvgData = signal<LogoSvgPosition>({
    logoX: "center",
    logoY: "0",
    logoWidth: "100%",
    logoHeight: "50%",
  });
  protected readonly positionTriangleData = signal<TrianglePosition>({
    height: "0",
    width: "0",
    top: "0",
    left: "0",
  });

  protected readonly design$: Observable<DesignKind>;
  protected isVisiblePassword = signal<boolean>(false);
  protected isFocus = signal<boolean>(false);

  // Computed для цвета иконки (вместо метода)
  // protected iconButtonColor = computed<string>(() => {
  //   const isInvalid =
  //     this.loginForm.controls.password.invalid &&
  //     this.loginForm.controls.password.touched;
  //   if (isInvalid) return "#b3261e";
  //   return this.isFocus() ? "#3b62ea" : "#ffffff";
  // });

  constructor(
    private httpService: HttpService,
    private reportNotificationService: ReportNotificationService,
    private router: Router,
    private localStorage: LocalStorageService,
    private translateService: TranslateService,
    private designService: DesignService,
    private cdr: ChangeDetectorRef,
    private renderer: Renderer2
  ) {
    this.design$ = this.designService.design$;

    this.loginForm.valueChanges.pipe(takeUntilDestroyed()).subscribe(() => {
      this.errorStatusMsg.set(null);
    });
  }

  ngOnInit() {}

  @HostListener("window:resize")
  onResize(): void {
    this.updateMask()
  }

  login() {
    const userData = this.loginForm.value;
    this.isLoading.set(true);
    this.errorStatusMsg.set(null);

    this.httpService
      .postResponse("user-service/auth", userData)
      .pipe(timeout(10000))
      .subscribe(
        (response) => {
          this.isLoading.set(false);

          if (response && response.headers.get("Authorization")) {
            setTimeout(
              (() => {
                this.reportNotificationService.fetchIsListerningNotification();
                this.reportNotificationService.initialization();
              }).bind(this),
              100
            );

            if (Array.isArray(response.body) && response.body.length) {
              const bearerToken = response.headers.get("Authorization");
              if (bearerToken.slice(0, 7) === "Bearer ") {
                this.localStorage.store("access_token", bearerToken);
                this.router.navigate(["/home"]);
              }
            } else {
              this.errorStatusMsg.set("notification.NO_AVAILABLE_PROJECTS");
            }
          } else {
            this.errorStatusMsg.set("notification.AUTH_SERVICE_ERROR");
          }
        },
        (error1) => {
          this.isLoading.set(false);

          switch (error1.status) {
            case 0: {
              this.errorStatusMsg.set("notification.AUTH_NETWORK_ERROR");
              break;
            }
            case 403: {
              this.errorStatusMsg.set("notification.AUTH_ACCESS_DENIED");
              break;
            }
            case 500:
            case 404: {
              this.errorStatusMsg.set("notification.AUTH_SERVICE_ERROR");
              break;
            }
            default: {
              this.translateService
                .get("notification.NETWORK_ERROR_WITH_CODE", {
                  value: error1.status,
                })
                .subscribe((translated) => {
                  this.errorStatusMsg.set(translated);
                });
            }
          }
        }
      );
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.updateMask(), 0);
  }

  protected getColorIconButton() {
    return this.loginForm.controls.password.invalid &&
      this.loginForm.controls.password.touched
      ? "#b3261e"
      : this.isFocus()
      ? "#3b62ea"
      : "#ffffff";
  }

  protected updateMask(): void {
    this.positionInputData.set([]);

    if (!this.input1 || !this.input2) return;

    const overlayRect = this.overlay.nativeElement.getBoundingClientRect();
    const logoSvg = this.logoSvg.nativeElement.getBoundingClientRect();

    this.positionInputCalculation(this.input1.nativeElement, overlayRect);
    this.positionInputCalculation(this.input2.nativeElement, overlayRect);
    this.positionLogoSvgCalculation(logoSvg, overlayRect);

    if (this.logoSvg) {
      const logoRect = this.logoSvg.nativeElement.getBoundingClientRect();
      this.positionTriangleCalculation(logoRect, overlayRect);
      this.maskInitPosition();
    }
  }

  protected onVisiblePassword() {
    this.isFocus.set(true);
    this.renderer.selectRootElement("#pass").focus();
    this.isVisiblePassword.update((prev) => !prev);
  }

  protected onFocus(): void {
    this.isFocus.set(true);
  }

  protected onFocusOut(): void {
    this.isFocus.set(false);
  }

  private positionInputCalculation(
    input: HTMLInputElement,
    overlayRect: DOMRect
  ) {
    const rect = input.getBoundingClientRect();
    const x = rect.left - overlayRect.left;
    const y = rect.top - overlayRect.top;
    const width = rect.width;
    const height = rect.height;

    const newPositionObj = { x, y, width, height };

    this.positionInputData.update((data) => [...data, newPositionObj]);
  }

  private positionLogoSvgCalculation(
    logoSvg: DOMRect,
    overlayRect: DOMRect
  ): void {
    const logoX = logoSvg.left - overlayRect.left + "px";
    const logoY = logoSvg.top - overlayRect.top + "px";
    const logoWidth = logoSvg.width + "px";
    const logoHeight = logoSvg.height + "px";

    this.positionLogoSvgData.set({
      logoX,
      logoY,
      logoWidth,
      logoHeight,
    });
  }

  private positionTriangleCalculation(
    logoSvg: DOMRect,
    overlayRect: DOMRect
  ): void {
    const triangleSize = logoSvg.height * 0.2;
    const width = triangleSize + "px";
    const height = triangleSize + "px";
    const left = logoSvg.left - overlayRect.left + logoSvg.width * 0.493 + "px";
    const top = logoSvg.top - overlayRect.top + logoSvg.height * 0.25 + "px";

    this.positionTriangleData.set({ width, height, left, top });
  }

  private maskInitPosition(): void {
    this.overlayFive.nativeElement.style.maskImage =
      "url(#blur-mask), url('../../../../../assets/images/layout/layout-main-logo.svg')";
    this.overlayFive.nativeElement.style.webkitMaskImage =
      "url(#blur-mask), url('../../../../../assets/images/layout/layout-main-logo.svg')";

    this.overlayFive.nativeElement.style.maskComposite = "exclude";
    this.overlayFive.nativeElement.style.webkitMaskComposite = "xor";

    this.overlayFive.nativeElement.style.maskRepeat = "no-repeat, no-repeat";
    this.overlayFive.nativeElement.style.webkitMaskRepeat =
      "no-repeat, no-repeat";

    this.overlayFive.nativeElement.style.maskPosition = `0 0, ${
      this.positionLogoSvgData().logoX
    } ${this.positionLogoSvgData().logoY}`;
    this.overlayFive.nativeElement.style.webkitMaskPosition = `0 0, ${
      this.positionLogoSvgData().logoX
    } ${this.positionLogoSvgData().logoY}`;

    this.overlayFive.nativeElement.style.maskSize = `100% 100%, ${
      this.positionLogoSvgData().logoWidth
    } ${this.positionLogoSvgData().logoHeight}`;
    this.overlayFive.nativeElement.style.webkitMaskSize = `100% 100%, ${
      this.positionLogoSvgData().logoWidth
    } ${this.positionLogoSvgData().logoHeight}`;
  }
}
